<template>
  <dashboard-layout title="Style Profiles" subtitle="Per-user learned tone & preferences">
    <template #actions>
      <search-box v-model="search" placeholder="Search user / reason…" />
      <button class="refresh-btn" @click="load" :disabled="loading">{{ loading ? 'Loading…' : 'Refresh' }}</button>
    </template>

    <div v-if="error" class="error-banner">⚠ {{ error }}</div>

    <loading-state v-if="loading && !profiles.length" label="Loading style profiles…" />

    <template v-else>
      <stat-grid>
        <stat-card label="Profiles learned" :value="profiles.length.toLocaleString('en-US')" icon="brush" accent />
        <stat-card label="Total updates" :value="logs.length.toLocaleString('en-US')" icon="activity" />
        <stat-card label="Avg revision" :value="avgRevision" icon="sliders" />
      </stat-grid>

      <empty-state
        v-if="!profiles.length"
        title="Henüz profil öğrenilmedi"
        icon="brush"
      >
        No user has a learned <code>styleProfile</code> yet. Records appear here as the backend
        <code>updateStyleProfile</code> function runs.
      </empty-state>

      <template v-else>
        <!-- List -->
        <data-panel flush>
          <template #title>Profiles <span class="muted">({{ filteredProfiles.length.toLocaleString('en-US') }})</span></template>
          <data-table
            :columns="columns"
            :rows="filteredProfiles"
            row-key="uid"
            clickable
            :expanded-key="expandedId"
            @row-click="toggleRow"
          >
            <template #cell-user="{ row }">
              <div class="user-cell">
                <span class="u-name"><span class="caret" :class="{ open: expandedId === row.uid }">▸</span> {{ row.name || '—' }}</span>
                <span class="u-id">{{ truncate(row.uid, 16) }}</span>
              </div>
            </template>
            <template #cell-revision="{ row }">
              <status-badge variant="primary" :label="'v' + (row.styleProfile.revision || 0)" />
            </template>
            <template #cell-updated="{ row }">
              <span class="nowrap">{{ formatDate(row.styleProfile.updatedAt) }}</span>
            </template>
            <template #cell-lastReason="{ row }">
              <span class="reason" :title="row.styleProfile.lastReason">{{ row.styleProfile.lastReason || '—' }}</span>
            </template>
            <template #empty>No profiles match your search</template>

            <template #expanded="{ row }">
              <div class="profile-detail">
                <div class="pd-head">
                  <router-link :to="`/user/${row.uid}`" class="user-link">{{ row.name || truncate(row.uid, 20) }}</router-link>
                  <span class="muted"> · v{{ spOf(row).revision || 0 }}</span>
                </div>

                <!-- Enums -->
                <div class="prefs-grid">
                  <div v-for="e in enumFields" :key="e.key" class="pref">
                    <span class="pref-label">{{ e.label }}</span>
                    <status-badge v-if="spOf(row)[e.key]" :variant="e.variant" :label="spOf(row)[e.key]" />
                    <span v-else class="muted">—</span>
                  </div>
                </div>

                <!-- Likes / dislikes -->
                <div class="chips-row" v-if="(spOf(row).likes && spOf(row).likes.length) || (spOf(row).dislikes && spOf(row).dislikes.length)">
                  <div v-if="spOf(row).likes && spOf(row).likes.length" class="chips-col">
                    <div class="chips-title">Likes</div>
                    <div class="chip-wrap">
                      <span v-for="(l, i) in spOf(row).likes" :key="'like' + i" class="pref-chip like">{{ l }}</span>
                    </div>
                  </div>
                  <div v-if="spOf(row).dislikes && spOf(row).dislikes.length" class="chips-col">
                    <div class="chips-title">Dislikes</div>
                    <div class="chip-wrap">
                      <span v-for="(d, i) in spOf(row).dislikes" :key="'dis' + i" class="pref-chip dislike">{{ d }}</span>
                    </div>
                  </div>
                </div>

                <!-- Sensitive topics -->
                <div v-if="spOf(row).sensitiveTopics && spOf(row).sensitiveTopics.length" class="sensitive">
                  <div class="chips-title">Sensitive topics</div>
                  <div v-for="(t, i) in spOf(row).sensitiveTopics" :key="'st' + i" class="sensitive-row">
                    <span class="st-topic">{{ t.topic }}</span>
                    <span class="st-arrow">→</span>
                    <span class="st-handling">{{ t.handling }}</span>
                  </div>
                </div>

                <!-- Evolution timeline -->
                <div class="timeline-block">
                  <div class="chips-title">Evolution <span class="muted">({{ logsFor(row).length }} update{{ logsFor(row).length === 1 ? '' : 's' }})</span></div>
                  <div v-if="!logsFor(row).length" class="muted small">No update logs found for this user.</div>
                  <div v-for="lg in logsFor(row)" :key="lg.id" class="tl-item">
                    <div class="tl-head">
                      <status-badge variant="primary" :label="'v' + (metaOf(lg).revision != null ? metaOf(lg).revision : '?')" />
                      <span class="tl-time">{{ formatDate(lg.createdAt) }}</span>
                    </div>
                    <div v-if="metaOf(lg).reason" class="tl-reason">“{{ metaOf(lg).reason }}”</div>
                    <div v-if="changeList(lg).length" class="tl-changes">
                      <span v-for="(c, i) in changeList(lg)" :key="i" class="change-chip">{{ c }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </data-table>
        </data-panel>
      </template>
    </template>
  </dashboard-layout>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { FirebaseStyleProfileService } from '../services/firebaseStyleProfileService'
import DashboardLayout from './ui/DashboardLayout.vue'
import StatGrid from './ui/StatGrid.vue'
import StatCard from './ui/StatCard.vue'
import DataPanel from './ui/DataPanel.vue'
import DataTable from './ui/DataTable.vue'
import SearchBox from './ui/SearchBox.vue'
import LoadingState from './ui/LoadingState.vue'
import EmptyState from './ui/EmptyState.vue'
import StatusBadge from './ui/StatusBadge.vue'

const columns = [
  { key: 'user', label: 'User' },
  { key: 'revision', label: 'Rev', width: '70px' },
  { key: 'updated', label: 'Updated', width: '160px' },
  { key: 'lastReason', label: 'Last reason' }
]

const enumFields = [
  { key: 'tone', label: 'Tone', variant: 'primary' },
  { key: 'length', label: 'Length', variant: 'info' },
  { key: 'depth', label: 'Depth', variant: 'info' },
  { key: 'humor', label: 'Humor', variant: 'success' },
  { key: 'questionsBack', label: 'Questions back', variant: 'warning' }
]

function toMs(ts) {
  if (!ts) return 0
  if (typeof ts === 'object' && typeof ts.toMillis === 'function') return ts.toMillis()
  if (typeof ts === 'object' && (ts.seconds || ts._seconds)) return (ts.seconds || ts._seconds) * 1000
  const t = new Date(ts).getTime()
  return isNaN(t) ? 0 : t
}

export default {
  name: 'StyleProfilesDashboard',
  components: {
    DashboardLayout, StatGrid, StatCard, DataPanel, DataTable, SearchBox, LoadingState, EmptyState, StatusBadge
  },
  setup() {
    const service = new FirebaseStyleProfileService()
    const profiles = ref([])
    const logs = ref([])
    const loading = ref(true)
    const error = ref('')
    const search = ref('')
    // Accordion: uid of the currently expanded row (null = none).
    const expandedId = ref(null)

    const load = async () => {
      loading.value = true
      error.value = ''
      try {
        const [p, l] = await Promise.all([service.getProfiles(), service.getStyleUpdateLogs()])
        profiles.value = p
        logs.value = l
      } catch (e) {
        console.error(e)
        const code = e && e.code ? e.code : ''
        if (code === 'permission-denied') error.value = 'Firestore denied the read (not signed in). Reload and try again.'
        else if (code === 'failed-precondition') error.value = (e && e.message) || 'A Firestore index is required for this query.'
        else error.value = (e && e.message) || 'Failed to load style profiles.'
      } finally {
        loading.value = false
      }
    }

    const filteredProfiles = computed(() => {
      const q = search.value.toLowerCase().trim()
      if (!q) return profiles.value
      return profiles.value.filter(p => {
        const hay = `${p.uid} ${p.name} ${p.styleProfile.lastReason || ''}`.toLowerCase()
        return hay.includes(q)
      })
    })

    const avgRevision = computed(() => {
      if (!profiles.value.length) return '0'
      const sum = profiles.value.reduce((s, p) => s + (Number(p.styleProfile.revision) || 0), 0)
      return (sum / profiles.value.length).toFixed(1)
    })

    const spOf = (row) => (row && row.styleProfile) || {}
    const logsFor = (row) => (row ? logs.value.filter(l => l.userId === row.uid) : [])
    const toggleRow = (row) => {
      expandedId.value = expandedId.value === row.uid ? null : row.uid
    }

    // metadata may be a JSON string or an object; normalise to an object.
    const metaCache = new Map()
    const metaOf = (log) => {
      const m = log.metadata
      if (!m) return {}
      if (typeof m === 'object') return m
      if (metaCache.has(m)) return metaCache.get(m)
      let parsed = {}
      try { parsed = JSON.parse(m) } catch (e) { parsed = {} }
      metaCache.set(m, parsed)
      return parsed
    }

    const changeList = (log) => {
      const changes = metaOf(log).changes
      if (!changes) return []
      if (Array.isArray(changes)) return changes.map(c => String(c))
      if (typeof changes === 'object') {
        return Object.keys(changes).map(k => {
          const v = changes[k]
          const val = (v && typeof v === 'object') ? JSON.stringify(v) : String(v)
          return `${k}: ${val}`
        })
      }
      return [String(changes)]
    }

    const truncate = (t, max) => !t ? '—' : (t.length > max ? t.slice(0, max) + '…' : t)
    const formatDate = (val) => {
      const ms = toMs(val)
      if (!ms) return '—'
      return new Date(ms).toLocaleString('tr-TR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
    }

    onMounted(load)

    return {
      profiles, logs, loading, error, search, expandedId,
      columns, enumFields, filteredProfiles, avgRevision, spOf, logsFor, toggleRow,
      load, metaOf, changeList, truncate, formatDate
    }
  }
}
</script>

<style scoped>
.error-banner {
  background: rgba(248, 191, 113, 0.12);
  border: 1px solid rgba(248, 191, 113, 0.35);
  color: var(--warning);
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
}
.refresh-btn {
  padding: 9px 14px;
  background: var(--field-bg);
  border: 1px solid var(--panel-border);
  border-radius: var(--radius);
  color: var(--text-primary);
  font-size: 13px;
  cursor: pointer;
}
.refresh-btn:hover { background: var(--hover-bg); }
.refresh-btn:disabled { opacity: 0.5; cursor: default; }

.muted { color: var(--text-secondary); font-weight: 400; }
.small { font-size: 12.5px; }
.nowrap { white-space: nowrap; }

.user-cell { display: flex; flex-direction: column; }
.u-name { color: var(--text-primary); font-weight: 500; }
.caret {
  display: inline-block;
  width: 11px;
  color: var(--text-secondary);
  transition: transform 0.18s ease;
}
.caret.open { transform: rotate(90deg); color: var(--primary-color); }

/* Inline expanded detail (accordion) */
.profile-detail { padding: 16px 18px 20px; }
.pd-head { margin-bottom: 14px; font-size: 15px; }
.u-id { font-size: 11px; color: var(--text-secondary); font-family: monospace; }
.reason {
  display: inline-block; max-width: 420px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; vertical-align: middle;
  color: var(--text-secondary);
}
.user-link { color: var(--primary-color); text-decoration: none; }
.user-link:hover { text-decoration: underline; }
.link-btn { background: none; border: none; color: var(--primary-color); cursor: pointer; font-size: 12.5px; padding: 0; }
.link-btn:hover { text-decoration: underline; }

/* Enums */
.prefs-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}
.pref { display: flex; flex-direction: column; gap: 6px; }
.pref-label { font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; color: var(--text-secondary); }

/* Chips */
.chips-row { display: flex; gap: 28px; flex-wrap: wrap; margin-bottom: 16px; }
.chips-col { min-width: 0; }
.chips-title { font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; color: var(--text-secondary); margin-bottom: 8px; }
.chip-wrap { display: flex; flex-wrap: wrap; gap: 6px; }
.pref-chip {
  padding: 4px 10px; border-radius: 14px; font-size: 12.5px; border: 1px solid transparent;
}
.pref-chip.like { background: rgba(74, 222, 128, 0.14); color: var(--success); border-color: rgba(74, 222, 128, 0.3); }
.pref-chip.dislike { background: rgba(255, 107, 107, 0.14); color: var(--danger); border-color: rgba(255, 107, 107, 0.32); }

/* Sensitive topics */
.sensitive { margin-bottom: 16px; }
.sensitive-row {
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
  padding: 8px 12px; background: var(--field-bg); border: 1px solid var(--panel-border);
  border-radius: var(--radius-sm); margin-bottom: 6px;
}
.st-topic { font-weight: 600; color: var(--text-primary); }
.st-arrow { color: var(--text-secondary); }
.st-handling { color: var(--text-secondary); }

/* Timeline */
.timeline-block { border-top: 1px solid var(--border-soft); padding-top: 14px; }
.tl-item { padding: 10px 0; border-bottom: 1px solid var(--border-soft); }
.tl-item:last-child { border-bottom: none; }
.tl-head { display: flex; align-items: center; gap: 10px; }
.tl-time { font-size: 12px; color: var(--text-secondary); }
.tl-reason { margin: 6px 0 0; font-size: 13.5px; color: var(--text-primary); font-style: italic; }
.tl-changes { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
.change-chip {
  font-size: 11.5px; padding: 3px 9px; border-radius: 12px; font-family: monospace;
  background: rgba(var(--primary-rgb), 0.14); color: var(--gradient-2); border: 1px solid rgba(var(--primary-rgb), 0.3);
}
code { font-size: 12px; background: var(--field-bg); padding: 1px 5px; border-radius: 4px; }
</style>
